import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, StatusPill, Table, EmptyState, Input, Select, useToast } from '../../design-system';
import PhysicalFileModal from '../components/PhysicalFileModal';
import ImportModal from '../components/ImportModal';
import { STATUS_LABEL, STATUS_VARIANT, STATUS_FILTERS, fmtDate } from '../../lib/physicalFiles';
import '../styles/PhysicalFiles.css';

function OrgAdminPhysicalFiles() {
  const toast = useToast();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(null); // null | { file } (file null = create)
  const [importOpen, setImportOpen] = useState(false);
  // ?client=<id> — where a client's "Physical files" utility lands. There is no
  // client dropdown on this page (the search box already matches client names),
  // so the filter announces itself as a removable chip instead of hiding in a
  // control nobody set.
  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilter = searchParams.get('client') || '';
  const clearClientFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('client');
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/physical-files');
      setFiles(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load physical files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const s = { total: files.length, active: 0, archived: 0, handed_over: 0 };
    files.forEach((f) => { if (s[f.status] !== undefined) s[f.status] += 1; });
    return s;
  }, [files]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return files.filter((f) => {
      if (clientFilter && String(f.clientId?._id || f.clientId) !== clientFilter) return false;
      if (statusFilter && f.status !== statusFilter) return false;
      if (!q) return true;
      return [f.fileNumber, f.clientId?.displayName, f.clientId?.clientCode, f.title, f.location, f.remarks]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [files, search, statusFilter, clientFilter]);

  // Named from the rows themselves — this page never fetches clients, and one
  // request for a name the table already carries would be a round trip for
  // nothing. Falls back when the client has no files at all, which is exactly
  // when the chip matters most: an empty table needs to say WHY it is empty.
  const clientFilterName = useMemo(() => {
    if (!clientFilter) return '';
    const hit = files.find((f) => String(f.clientId?._id || f.clientId) === clientFilter);
    return hit?.clientId?.displayName || 'this client';
  }, [files, clientFilter]);

  const remove = async (file) => {
    if (!window.confirm(`Delete file "${file.fileNumber}"?`)) return;
    try {
      await apiClient.delete(`/api/physical-files/${file._id}`);
      toast.success('File deleted');
      load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to delete file');
    }
  };

  const statCards = [
    { label: 'Total', value: stats.total, tone: '', filter: '' },
    { label: 'Active', value: stats.active, tone: 'success', filter: 'active' },
    { label: 'Archived', value: stats.archived, tone: '', filter: 'archived' },
    { label: 'Handed over', value: stats.handed_over, tone: 'info', filter: 'handed_over' },
  ];

  const columns = [
    { key: 'fileNumber', header: 'File No.', render: (f) => <span className="pf-number">{f.fileNumber}</span> },
    { key: 'client', header: 'Client', render: (f) => (
      <div>
        <span className="pf-client-name">{f.clientId?.displayName || '—'}</span>
        {f.clientId?.clientCode && <span className="pf-client-code">{f.clientId.clientCode}</span>}
      </div>
    ) },
    { key: 'title', header: 'Title', render: (f) => f.title || '—' },
    { key: 'location', header: 'Location', render: (f) => f.location || '—' },
    { key: 'status', header: 'Status', render: (f) => <StatusPill variant={STATUS_VARIANT[f.status]} label={STATUS_LABEL[f.status]} /> },
    { key: 'created', header: 'Allocated', render: (f) => fmtDate(f.createdAt) },
    {
      key: 'actions',
      width: 130,
      header: '',
      render: (f) => (
        <div className="pf-row-actions" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="secondary" onClick={() => setModal({ file: f })}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => remove(f)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <OrgAdminLayout title="Physical Files" subtitle="Allocate and track clients' physical files in the cabinet">
      <div className="pf-page">
        <div className="pf-stats">
          {statCards.map((c) => (
            <Card
              key={c.label}
              className={`pf-stat pf-stat-clickable${c.tone ? ` ${c.tone}` : ''}${statusFilter === c.filter ? ' active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setStatusFilter(c.filter)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStatusFilter(c.filter); } }}
            >
              <div className="pf-stat-num">{c.value}</div>
              <div className="pf-stat-label">{c.label}</div>
            </Card>
          ))}
        </div>

        <Card className="pf-toolbar-card">
          <div className="pf-toolbar">
            <Input
              aria-label="Search physical files"
              placeholder="Search file no., client, title, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select aria-label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Select>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>Import</Button>
            <Button variant="primary" onClick={() => setModal({ file: null })}>+ Allocate File</Button>
          </div>
          {clientFilter && (
            <div className="pf-clientchip">
              Showing files for <b>{clientFilterName}</b>
              <button type="button" onClick={clearClientFilter} aria-label="Show all clients' files">
                Clear
              </button>
            </div>
          )}
        </Card>

        {!loading && files.length === 0 ? (
          <EmptyState icon="🗄️" title="No physical files yet" description="Allocate a file number to a client to start tracking its physical folder." />
        ) : !loading && filtered.length === 0 ? (
          <EmptyState
            icon="🔍"
            title={clientFilter ? 'No files for this client' : 'No matches'}
            description={clientFilter
              ? 'No physical file has been allocated to them yet. Use "Allocate File" to create one.'
              : 'No files match your search or filter.'}
          />
        ) : (
          <Card>
            <Table columns={columns} data={filtered} loading={loading} />
          </Card>
        )}
      </div>

      {modal && (
        <PhysicalFileModal
          file={modal.file}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
        basePath="/api/physical-files"
        title="Import physical files from a spreadsheet"
        noun="file"
        nounPlural="files"
        templateFile="physical-files-import-template.xlsx"
        describeTemplate="An Excel file with the columns we accept, a dropdown for Status, and a sheet listing your clients. One row = one physical file."
        describeUpload="Leave File Number blank on any row to have one assigned automatically (PF-0001, PF-0002…)."
        rowLabel={(r) => r.fileNumber}
      />
    </OrgAdminLayout>
  );
}

export default OrgAdminPhysicalFiles;
